<script setup>
import { ref, reactive, computed, h, watch, onMounted, onUnmounted } from 'vue'
import {
  useVueTable,
  getCoreRowModel,
  createColumnHelper,
  FlexRender
} from '@tanstack/vue-table'
import {
  getAvailableDescriptors,
  resolveCellValue,
  descriptorSlug,
  filterKeyForDescriptor,
  getValue,
} from '@/config/ipTableFields'
import CellFilterButtons from '@/components/Selection/CellFilterButtons.vue'
import CellContextMenu from '@/components/Selection/CellContextMenu.vue'
import { useCellSelection } from '@/composables/useCellSelection'
import { useColumnStatsHover } from '@/composables/useColumnStatsHover'
import ColumnStatsPopover from '@/components/EventSearch/ColumnStatsPopover.vue'
import { QUEUE_MODES, FILTER_OPERATORS, makeSelectionItem, parityKey, scalarLeaves, isEmptyValue } from '@/utils/selection'

// Import widget components
import Country from '@/components/Query/Widget/Country.vue'
import IpWidget from '@/components/Query/Widget/Ip.vue'
import TagsBasic from '@/components/Query/Widget/TagsBasic.vue'
import Observations from '@/components/Query/Widget/Observations.vue'
import Anonymization from '@/components/Query/Widget/Anonymization.vue'
import OsintProviders from '@/components/Query/Widget/OsintProviders.vue'
import Signals from '@/components/Query/Widget/Signals.vue'
import CellValueChips from '@/components/Selection/CellValueChips.vue'
import Generic from '@/components/Query/Widget/Generic.vue'
import AbuseScore from '@/components/Query/Widget/AbuseScore.vue'
import AbuseReports from '@/components/Query/Widget/AbuseReports.vue'

// The full root dot-path to a descriptor's value (used for shift-select
// filtering, which resolves item.field via getValue from the row root).
function filterFieldFor(descriptor) {
  if (!descriptor) return ''
  if (descriptor.common) return descriptor.accessor
  const slug = descriptor.slug || descriptorSlug(descriptor.id)
  return descriptor.accessor ? `enrichments.${slug}.${descriptor.accessor}` : `enrichments.${slug}`
}

const props = defineProps({
  data: {
    type: Array,
    default: () => []
  },
  enabledFields: {
    type: Array,
    default: () => []
  },
  pendingSideload: {
    type: Set,
    default: () => new Set()
  },
  // Opt-in drill-in (Selector Search zoom), fired from the cell magnifier
  // button and Enter. Off by default so existing consumers keep plain rows.
  rowClickable: {
    type: Boolean,
    default: false
  },
  // Opt-in cell interactions (Kibana-style). Off by default.
  selectable: {
    type: Boolean,
    default: false
  },
  cellFilters: {
    type: Boolean,
    default: false
  },
  contextMenu: {
    type: Boolean,
    default: false
  },
  // Optional shared selection instance from a parent that wants to read the
  // selection / applied-filter state. Falls back to a private instance.
  selection: {
    type: Object,
    default: null
  },
  // When the parent owns filtering (e.g. the results view's query bar), the
  // table's built-in text search and applied-filter chips are suppressed and
  // `data` is treated as already-filtered (sorting still applies).
  externalFilter: {
    type: Boolean,
    default: false
  },
  // Stats provider for the column header popover: (field) => { terms, cardinality }.
  // Null leaves headers inert, so consumers without one get a plain table.
  columnStats: {
    type: Function,
    default: null
  }
})

// Filtering is owned by the parent: the table emits intents and never applies
// them itself, so the backend (client-side match today, an API later) is
// swappable in one place. The parent hands back the already-filtered rows as
// `data`; `filtered-change` reports the text-searched/sorted rows for export.
const emit = defineEmits(['filtered-change', 'row-click', 'apply-filter', 'toggle-contains', 'remove-filter'])

const sel = props.selection || useCellSelection()

// Ctrl/Cmd+C copies the selected cells as a spreadsheet TSV grid. Only fires
// when this table has a selection and focus isn't in a field / no real text is
// selected, so native copy still works everywhere else.
function onCopyKey(e) {
  if (e.key !== 'c' || !(e.ctrlKey || e.metaKey)) return
  if (!sel.hasSelection.value) return
  const el = document.activeElement
  if (el?.tagName === 'INPUT' || el?.tagName === 'TEXTAREA' || el?.isContentEditable) return
  if (String(window.getSelection?.() || '').length > 0) return
  e.preventDefault()
  sel.copySelectionGrid()
}
onMounted(() => document.addEventListener('keydown', onCopyKey))
onUnmounted(() => document.removeEventListener('keydown', onCopyKey))

function operatorLabel(op) {
  if (op === FILTER_OPERATORS.ALL) return 'AND'
  if (op === FILTER_OPERATORS.NOT) return 'NOT'
  return 'OR'
}

// Resolve a cell to a whole-cell filter descriptor (used for area-select
// enumeration, hover +/-, and highlight). Every cell resolves to something so
// all cells stay selectable/filterable regardless of data:
//   empty        -> filter on field existence
//   scalar       -> field:value
//   Location     -> its country ISO (scalar)
//   structured   -> PARITY (whole-cell exact match); per-token filtering is a
//                   separate path (widget provider chips -> emit('toggle-contains')).
function resolveCellScalar(cell) {
  const meta = cell.column.columnDef.meta || {}
  // In externalFilter mode the parent filters via a readable query string, so
  // filter tokens use the column's clean filter key ('location', as shown in
  // autocomplete) instead of the nested `enrichments.<slug>.<accessor>` path.
  const ext = props.externalFilter
  const field = ext ? filterKeyForDescriptor(meta.descriptor) : (meta.filterField || cell.column.id)
  let rawValue = cell.getValue()
  // Whole-blob widgets that filter on one scalar (abuseipdb score/reports,
  // observations count) narrow to that leaf so the +/- term is `score:80`, not
  // an opaque whole-blob match.
  if (ext && meta.descriptor?.filterAccessor && rawValue && typeof rawValue === 'object') {
    rawValue = getValue(rawValue, meta.descriptor.filterAccessor)
  }
  if (isEmptyValue(rawValue)) {
    return { field, rawValue: '', formattedValue: '(empty)', mode: QUEUE_MODES.FIELD_VALUE, empty: true }
  }
  if (typeof rawValue !== 'object') {
    return { field, rawValue, formattedValue: String(rawValue), mode: QUEUE_MODES.FIELD_VALUE }
  }
  const descriptor = meta.descriptor
  if (descriptor && descriptor.widget === 'Country') {
    const loc = resolveCellValue(cell.row.original, descriptor)
    const iso = Array.isArray(loc) ? loc[0]?.country_iso : loc?.country_iso
    // externalFilter resolves `<col>` through the descriptor (which yields the
    // location object holding country_iso), so no `.country_iso` suffix.
    if (iso) return { field: ext ? field : `${field}.country_iso`, rawValue: iso, formattedValue: iso, mode: QUEUE_MODES.FIELD_VALUE }
  }
  const base = {
    field,
    rawValue: parityKey(rawValue),
    formattedValue: scalarLeaves(rawValue).map(String).join(', ') || '(value)',
    mode: QUEUE_MODES.PARITY,
  }
  if (ext) {
    // Whole-cell +/- fans out to clean per-value terms: a descriptor's
    // `filterValues` (e.g. anon provider labels) wins; else tag-list arrays fan
    // out per leaf; else opaque object widgets (Signals) have no clean whole-cell
    // value — their per-value chips are the filter path.
    if (descriptor?.filterValues) base.leaves = descriptor.filterValues(rawValue)
    else if (Array.isArray(rawValue)) base.leaves = scalarLeaves(rawValue).map(String)
    else base.structured = true
  }
  return base
}

// Expand a resolved cell (or stored selection item) into the query-string filter
// items it should contribute, in externalFilter mode: array cells fan out to one
// `col:value` term per leaf; opaque structured cells contribute nothing (use the
// chips); scalars/empties pass through as a single item.
function leafItems(r) {
  if (Array.isArray(r.leaves)) {
    return r.leaves.map(lv => makeSelectionItem(r.field, lv, String(lv), QUEUE_MODES.FIELD_VALUE, false))
  }
  if (r.structured) return []
  return [makeSelectionItem(r.field, r.rawValue, r.formattedValue, r.mode, r.empty)]
}

// Per-token filter from a widget provider chip (e.g. one anon provider). Emits
// a containment intent; the parent OR-groups it for the column.
function onProviderFilter(field, value, negate) {
  emit('toggle-contains', field, value, negate)
}

// Pill-aware resolution for direct clicks: a widget may mark an element with
// data-shift-value / data-shift-field so the click maps to a single pill.
function resolveCellFromEvent(cell, event) {
  let pillEl = event.target.closest?.('[data-shift-value]')
  if (!pillEl) {
    const td = event.currentTarget
    const pills = td?.querySelectorAll?.('[data-shift-value]')
    if (pills && pills.length === 1) pillEl = pills[0]
  }
  const fallbackField = props.externalFilter
    ? filterKeyForDescriptor(cell.column.columnDef.meta?.descriptor)
    : (cell.column.columnDef.meta?.filterField || cell.column.id)
  if (pillEl) {
    const value = pillEl.getAttribute('data-shift-value')
    if (value !== null && value !== '') {
      const field = pillEl.getAttribute('data-shift-field') || fallbackField
      return { field, rawValue: value, formattedValue: value, mode: QUEUE_MODES.FIELD_VALUE }
    }
  }
  return resolveCellScalar(cell)
}

function cellDescriptorByIndex(row, cell, rowIndex, colIndex) {
  const r = resolveCellScalar(cell)
  if (!r) return null
  return { rowId: row.id, colId: cell.column.id, rowIndex, colIndex, ...r }
}

function rectangleCells(anchor, target) {
  const rows = table.getRowModel().rows
  const r0 = Math.min(anchor.rowIndex, target.rowIndex)
  const r1 = Math.max(anchor.rowIndex, target.rowIndex)
  const c0 = Math.min(anchor.colIndex, target.colIndex)
  const c1 = Math.max(anchor.colIndex, target.colIndex)
  const cells = []
  for (let r = r0; r <= r1; r++) {
    const row = rows[r]
    if (!row) continue
    const visCells = row.getVisibleCells()
    for (let c = c0; c <= c1; c++) {
      const cell = visCells[c]
      if (!cell) continue
      const desc = cellDescriptorByIndex(row, cell, r, c)
      if (desc) cells.push(desc)
    }
  }
  return cells
}

function onCellMouseDown(event, row, cell, rowIndex, colIndex) {
  if (!props.selectable) return
  if (event.button !== 0) return
  if (event.target.closest?.('a, button, input')) return
  const r = resolveCellFromEvent(cell, event)
  if (!r) return
  const desc = { rowId: row.id, colId: cell.column.id, rowIndex, colIndex, ...r }
  if (event.shiftKey && sel.anchor.value) {
    event.preventDefault()
    sel.setRange(rectangleCells(sel.anchor.value, desc))
  } else if (event.ctrlKey || event.metaKey) {
    event.preventDefault()
    sel.toggle(desc)
  } else {
    sel.setSingleOrDeselect(desc)
  }
}

function onCellFilter(cell, negate) {
  const r = resolveCellScalar(cell)
  if (!r) return
  const op = negate ? FILTER_OPERATORS.NOT : FILTER_OPERATORS.ALL
  if (props.externalFilter) {
    const items = leafItems(r)
    if (items.length) emit('apply-filter', items, op)
    return
  }
  const item = makeSelectionItem(r.field, r.rawValue, r.formattedValue, r.mode, r.empty)
  emit('apply-filter', [item], op)
}

// Selected-cell items expanded for the query-string filter (externalFilter);
// identity otherwise.
function menuFilterItems() {
  return props.externalFilter ? menu.items.flatMap(leafItems) : menu.items
}

// Right-click context menu
const menu = reactive({ show: false, x: 0, y: 0, items: [] })

function onCellContextMenu(event, row, cell, rowIndex, colIndex) {
  if (!props.contextMenu) return
  event.preventDefault()
  if (!sel.isSelected(row.id, cell.column.id)) {
    const r = resolveCellFromEvent(cell, event)
    if (!r) return
    sel.setSingle({ rowId: row.id, colId: cell.column.id, rowIndex, colIndex, ...r })
  }
  const items = sel.selectedItems.value
  if (!items.length) return
  Object.assign(menu, { items, x: event.clientX, y: event.clientY, show: true })
}

function closeMenu() {
  menu.show = false
}

function menuFilterFor() {
  emit('apply-filter', menuFilterItems(), FILTER_OPERATORS.ALL)
  sel.clear()
}

function menuFilterOut() {
  emit('apply-filter', menuFilterItems(), FILTER_OPERATORS.NOT)
  sel.clear()
}

function menuFilterForAny() {
  emit('apply-filter', menuFilterItems().map(i => ({ ...i, mode: QUEUE_MODES.VALUE_ONLY })), FILTER_OPERATORS.ANYOF)
  sel.clear()
}

function menuBulk(operator) {
  emit('apply-filter', menuFilterItems(), operator)
  sel.clear()
}

function onRowEnter(row) {
  if (props.rowClickable) emit('row-click', row.original)
}

// Column stats popover. Fields are the clean per-column filter keys, matching
// what the cell +/- buttons emit and what the parent's filter bar completes on.
const {
  show: showStatsPopover,
  field: statsPopoverField,
  label: statsPopoverLabel,
  anchor: statsPopoverAnchor,
  onHeaderEnter: statsHeaderEnter,
  onHeaderLeave: handleHeaderMouseLeave,
  close: closeStatsPopover
} = useColumnStatsHover({ enabled: () => !!props.columnStats })

function handleHeaderMouseEnter(event, column) {
  const descriptor = column.columnDef.meta?.descriptor
  if (!descriptor) return
  statsHeaderEnter(event, filterKeyForDescriptor(descriptor), descriptor.label)
}

function handlePopoverAddFilter({ field, value, operator }) {
  emit('toggle-contains', field, value, operator === 'exclude')
  closeStatsPopover()
}

// Search filter state
const searchFilter = ref('')

// Sort state
const sortField = ref(null)
const sortDirection = ref(null) // 'asc', 'desc', or null

// Recursively concatenate every leaf value of a node into one lowercased
// string. Generic so the search covers all enrichment blobs, including
// sideloaded slugs the registry doesn't know about.
function flattenToString(node) {
  if (node === null || node === undefined) return ''
  if (typeof node !== 'object') return String(node).toLowerCase()
  let out = ''
  if (Array.isArray(node)) {
    for (const v of node) out += ' ' + flattenToString(v)
  } else {
    for (const k in node) out += ' ' + flattenToString(node[k])
  }
  return out
}

// Text search on top of props.data, which the parent has already filtered by
// the active applied filters.
const filteredData = computed(() => {
  const base = props.data
  if (props.externalFilter || !searchFilter.value.trim()) {
    return base
  }

  const query = searchFilter.value.toLowerCase().trim()

  return base.filter(row => {
    if (row.selector && String(row.selector).toLowerCase().includes(query)) return true
    return flattenToString(row.enrichments).includes(query)
  })
})

// Descriptors renderable for the current data, indexed by id. Used to resolve
// cell values for sorting and dynamic column construction.
const descriptorsById = computed(() => {
  const m = new Map()
  for (const d of getAvailableDescriptors(props.data)) m.set(d.id, d)
  return m
})

// Sorted data (applies sorting to filtered data)
const sortedData = computed(() => {
  if (!sortField.value || !sortDirection.value) {
    return filteredData.value
  }

  const descriptor = descriptorsById.value.get(sortField.value)
  if (!descriptor) return filteredData.value
  const dir = sortDirection.value

  return [...filteredData.value].sort((a, b) => {
    let aVal = resolveCellValue(a, descriptor)
    let bVal = resolveCellValue(b, descriptor)

    // Observations sort by total observation count.
    if (descriptor.widget === 'Observations') {
      aVal = aVal?.total_count ?? 0
      bVal = bVal?.total_count ?? 0
    }

    // AbuseIPDB widgets sort by their headline metric.
    if (descriptor.widget === 'AbuseScore') {
      aVal = aVal?.abuse_confidence_score ?? 0
      bVal = bVal?.abuse_confidence_score ?? 0
    }
    if (descriptor.widget === 'AbuseReports') {
      aVal = aVal?.total_reports ?? 0
      bVal = bVal?.total_reports ?? 0
    }

    // Handle null/undefined
    if (aVal == null && bVal == null) return 0
    if (aVal == null) return dir === 'asc' ? -1 : 1
    if (bVal == null) return dir === 'asc' ? 1 : -1

    // Handle arrays (compare first element or length)
    if (Array.isArray(aVal) && Array.isArray(bVal)) {
      const aLen = aVal.length
      const bLen = bVal.length
      return dir === 'asc' ? aLen - bLen : bLen - aLen
    }

    // Handle objects (try to get a sortable value)
    if (typeof aVal === 'object' && typeof bVal === 'object') {
      // For location, sort by country_iso
      const aStr = aVal.country_iso || aVal.name || JSON.stringify(aVal)
      const bStr = bVal.country_iso || bVal.name || JSON.stringify(bVal)
      return dir === 'asc'
        ? aStr.localeCompare(bStr)
        : bStr.localeCompare(aStr)
    }

    // Handle numbers
    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return dir === 'asc' ? aVal - bVal : bVal - aVal
    }

    // Handle strings
    const aStr = String(aVal).toLowerCase()
    const bStr = String(bVal).toLowerCase()
    return dir === 'asc'
      ? aStr.localeCompare(bStr)
      : bStr.localeCompare(aStr)
  })
})

watch(sortedData, (rows) => emit('filtered-change', rows), { immediate: true })

// Sort helper functions
function getSortState(fieldName) {
  if (sortField.value !== fieldName) return null
  return { direction: sortDirection.value }
}

function handleHeaderClick(event, column) {
  const fieldName = column.id
  if (sortField.value === fieldName) {
    // Cycle: asc -> desc -> none
    if (sortDirection.value === 'asc') {
      sortDirection.value = 'desc'
    } else if (sortDirection.value === 'desc') {
      sortField.value = null
      sortDirection.value = null
    }
  } else {
    // New field, start with asc
    sortField.value = fieldName
    sortDirection.value = 'asc'
  }
}

// Build column helper
const columnHelper = createColumnHelper()

// Build a single TanStack column def from a widget descriptor.
function buildColumn(descriptor, pending) {
  const filterField = filterFieldFor(descriptor)
  // Token widgets (provider/tag chips, data-shift-field) filter through this
  // field. In externalFilter mode it's the column's clean filter key (readable,
  // matches autocomplete); otherwise the nested enrichment path.
  const emitField = props.externalFilter ? filterKeyForDescriptor(descriptor) : filterField
  const slug = descriptor.common ? null : (descriptor.slug || descriptorSlug(descriptor.id))

  return columnHelper.accessor(row => resolveCellValue(row, descriptor), {
    id: descriptor.id,
    header: descriptor.label,
    size: descriptor.width || undefined,
    meta: { descriptor, filterField },
    cell: info => {
      const value = info.getValue()
      const row = info.row.original
      // Sideloaded widgets show a spinner until their slug lands for this row.
      // Pending keys are `${slug}::${selector}` (useSelectorFetch's pass B).
      const isPending = slug ? pending.has(`${slug}::${row.selector}`) : false

      switch (descriptor.widget) {
        case 'Country':
          return h(Country, {
            data: value,
            field: props.externalFilter ? emitField : `${filterField}.country_iso`
          })
        case 'IpWidget':
          return h(IpWidget, {
            data: value
          })
        case 'TagsBasic':
          return h(TagsBasic, {
            data: value,
            maxlen: 1,
            nostyle: true,
            field: emitField
          })
        case 'Observations':
          return h(Observations, {
            data: value,
            rowData: row,
            value: row.selector,
            pending: isPending
          })
        case 'AbuseScore':
          return h(AbuseScore, { data: value, pending: isPending })
        case 'AbuseReports':
          return h(AbuseReports, { data: value, pending: isPending })
        case 'Anonymization':
          return h(Anonymization, {
            data: value,
            field: emitField,
            filterable: props.cellFilters,
            onProviderFilter: ({ value: pv, negate }) => onProviderFilter(emitField, pv, negate)
          })
        case 'OsintProviders':
          return h(OsintProviders, { data: value })
        case 'Signals':
          return h(Signals, { data: value, width: descriptor.width })
        case 'Tags':
          // String-array columns (e.g. Company Business) render like the
          // Anonymization cell: first value inline, the rest behind a "+N"
          // dropdown with per-value +/- filtering. Each value is data-shift
          // tagged so resolveCellFromEvent can select a single one, and the
          // per-value +/- OR-groups through the same toggle-contains path as
          // the anon providers.
          return h(CellValueChips, {
            leaves: scalarLeaves(value),
            field: emitField,
            empty: isEmptyValue(value),
            filters: props.cellFilters,
            onFilter: (payload, negate) => {
              if (!payload.empty) onProviderFilter(emitField, payload.value, negate)
            }
          })
        case 'Generic':
          return h(Generic, {
            data: value,
            field: filterField || `enrichments.${slug}`
          })
        case 'Text':
        default:
          if (value && typeof value === 'object') return ''
          return value ?? ''
      }
    }
  })
}

// Build table columns dynamically: available descriptors (common + present
// enrichment slugs, registry widgets or a generic fallback), ordered and
// filtered by the enabled-fields list.
const tableColumns = computed(() => {
  // Read pendingSideload so the computed re-evaluates whenever the pending set
  // changes — this rebuilds cell render closures with the fresh `pending` flag
  // and triggers a table re-render.
  const pending = props.pendingSideload
  const available = descriptorsById.value

  const order = props.enabledFields.length > 0
    ? props.enabledFields
    : [...available.keys()]

  const cols = []
  for (const id of order) {
    const descriptor = available.get(id)
    if (!descriptor) continue   // not available for the current data
    cols.push(buildColumn(descriptor, pending))
  }
  return cols
})

// Create table instance
const table = useVueTable({
  get data() {
    return sortedData.value
  },
  get columns() {
    return tableColumns.value
  },
  getCoreRowModel: getCoreRowModel(),
  getRowId: (row) => row.selector || String(Math.random())
})

function clearFilter() {
  searchFilter.value = ''
}
</script>

<template>
  <div class="ip-results-table">
    <!-- Empty state (no data at all) -->
    <div v-if="data.length === 0" class="state-overlay">
      <div class="state-box">
        <span class="state-message">No results</span>
      </div>
    </div>

    <!-- Has data -->
    <template v-else>
      <!-- Table Container -->
      <div class="table-container">
        <!-- No matches for filter overlay (inside table container) -->
        <div v-if="filteredData.length === 0" class="filter-empty-overlay">
          <div class="state-box">
            <span class="state-message">No matching results</span>
            <span class="state-hint">Try adjusting your filter</span>
          </div>
        </div>
        <div class="table-scroll">
          <table>
            <thead>
              <tr v-for="headerGroup in table.getHeaderGroups()" :key="headerGroup.id">
                <th v-if="rowClickable" class="caret-col"></th>
                <th
                  v-for="header in headerGroup.headers"
                  :key="header.id"
                  :style="{ width: header.getSize() ? `${header.getSize()}px` : 'auto' }"
                  class="sortable-header"
                  @click="handleHeaderClick($event, header.column)"
                  @mouseenter="handleHeaderMouseEnter($event, header.column)"
                  @mouseleave="handleHeaderMouseLeave($event)"
                >
                  <div class="header-content">
                    <span class="header-label">
                      <FlexRender
                        :render="header.column.columnDef.header"
                        :props="header.getContext()"
                      />
                    </span>
                    <span class="sort-indicator" :class="{ 'sort-active': getSortState(header.column.id) }">
                      {{ getSortState(header.column.id)?.direction === 'asc' ? '▲' : '▼' }}
                    </span>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody :class="{ 'selectable': selectable }">
              <tr
                v-for="(row, rowIndex) in table.getRowModel().rows"
                :key="row.id"
                :class="{ 'row-clickable': rowClickable }"
                :tabindex="rowClickable ? 0 : undefined"
                @keydown.enter="onRowEnter(row)"
              >
                <td v-if="rowClickable" class="caret-col">
                  <button
                    class="caret-btn"
                    title="Zoom in"
                    @click.stop="emit('row-click', row.original)"
                    @mousedown.stop
                  >
                    <font-awesome-icon :icon="['fas', 'chevron-right']" />
                  </button>
                </td>
                <td
                  v-for="(cell, colIndex) in row.getVisibleCells()"
                  :key="cell.id"
                  :class="{
                    'cell-selected': selectable && sel.isSelected(row.id, cell.column.id),
                    'has-cell-filters': cellFilters
                  }"
                  @mousedown="onCellMouseDown($event, row, cell, rowIndex, colIndex)"
                  @contextmenu="onCellContextMenu($event, row, cell, rowIndex, colIndex)"
                >
                  <div class="cell-content">
                    <FlexRender
                      :render="cell.column.columnDef.cell"
                      :props="cell.getContext()"
                    />
                  </div>
                  <CellFilterButtons
                    v-if="cellFilters"
                    @filter-for="onCellFilter(cell, false)"
                    @filter-out="onCellFilter(cell, true)"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Table Footer (always visible when there's data) -->
      <div class="table-footer">
        <!-- Search Filter + Selection chip (left side) -->
        <div class="footer-left">
          <div v-if="!externalFilter" class="search-container" :class="{ 'has-value': searchFilter }">
            <font-awesome-icon class="search-icon" :icon="['fas', 'magnifying-glass']" />
            <input
              v-model="searchFilter"
              class="search-input"
              placeholder="Filter results..."
              type="text"
            />
            <button v-if="searchFilter" class="search-clear" @click="clearFilter">
              <font-awesome-icon :icon="['fas', 'xmark']" />
            </button>
          </div>
          <div v-if="selectable && sel.hasSelection.value" class="selection-count">
            <span class="sel-count">{{ sel.count.value }}</span>
            <span class="sel-label">{{ sel.count.value === 1 ? 'cell' : 'cells' }} selected</span>
            <button class="sel-clear" @click="sel.clear()" title="Clear selection">
              <font-awesome-icon :icon="['fas', 'xmark']" />
            </button>
          </div>
        </div>

        <!-- Result Count (center) -->
        <div class="result-info">
          <span v-if="searchFilter">
            {{ filteredData.length }} of {{ data.length.toLocaleString() }} results
          </span>
          <span v-else>
            {{ data.length.toLocaleString() }} results
          </span>
        </div>

        <!-- Right side: accumulated applied filters -->
        <div class="footer-right">
          <div v-if="!externalFilter && sel.appliedFilters.value.length > 0" class="active-filters">
            <div v-for="f in sel.appliedFilters.value" :key="f.id" class="filter-tag">
              <span class="filter-field">{{ operatorLabel(f.operator) }}:</span>
              <span class="filter-value">{{ f.items.length }} {{ f.items.length === 1 ? 'value' : 'values' }}</span>
              <button class="filter-remove" @click="emit('remove-filter', f.id)" title="Remove filter">
                <font-awesome-icon :icon="['fas', 'xmark']" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- Cell Context Menu -->
    <CellContextMenu
      :show="menu.show"
      :x="menu.x"
      :y="menu.y"
      :items="menu.items"
      :count="sel.count.value"
      @filter-for="menuFilterFor"
      @filter-out="menuFilterOut"
      @filter-for-any="menuFilterForAny"
      @apply-bulk="menuBulk"
      @open-selector="sel.openInSelectorSearch(menu.items)"
      @open-event="sel.openInEventSearch(menu.items)"
      @copy="sel.copyItems(menu.items)"
      @close="closeMenu"
    />

    <!-- Column Stats Popover -->
    <ColumnStatsPopover
      v-if="columnStats && statsPopoverField"
      :show="showStatsPopover"
      :field="statsPopoverField"
      :label="statsPopoverLabel"
      :provider="columnStats"
      :anchor-el="statsPopoverAnchor"
      @add-filter="handlePopoverAddFilter"
      @close="closeStatsPopover"
    />
  </div>
</template>

<style scoped>
.ip-results-table {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  background: var(--surface-card);
  border-radius: var(--radius-lg);
  overflow: hidden;
  position: relative;
}

/* State Overlay (when no data at all) */
.state-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(21, 26, 34, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 5;
}

.state-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 2rem 3rem;
  background: var(--surface-page);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-lg);
}

.state-message {
  color: var(--text-primary);
  font-size: 0.95rem;
  text-align: center;
}

.state-hint {
  color: var(--text-faint);
  font-size: 0.8rem;
  text-align: center;
}

/* Table Container */
.table-container {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
}

/* Filter empty overlay (contained within table-container) */
.filter-empty-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(21, 26, 34, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 5;
}

.table-scroll {
  flex: 1;
  min-height: 0;
  overflow-x: auto;
  overflow-y: auto;
}

/* Scrollbar styling */
.table-scroll::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.table-scroll::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.2);
  border-radius: var(--radius-md);
}

.table-scroll::-webkit-scrollbar-thumb {
  background: linear-gradient(180deg, #4A5661 0%, #3E4A56 100%);
  border-radius: var(--radius-md);
  border: 2px solid rgba(0, 0, 0, 0.2);
}

.table-scroll::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(180deg, #5A6671 0%, #4E5A66 100%);
}

.table-scroll::-webkit-scrollbar-corner {
  background: rgba(0, 0, 0, 0.2);
}

/* Table Styling */
table {
  min-width: 100%;
  width: max-content;
  border-collapse: separate;
  border-spacing: 0;
  table-layout: fixed;
}

thead {
  position: sticky;
  top: 0;
  z-index: 2;
}

thead th {
  background-color: var(--surface-card);
  color: var(--text-muted);
  text-align: left;
  padding: 1rem 1rem;
  font-weight: 600;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  border-bottom: 1px solid var(--border-subtle);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Sortable Headers */
thead th.sortable-header {
  cursor: pointer;
  user-select: none;
  transition: background-color 0.15s ease;
}

thead th.sortable-header:hover {
  background-color: var(--surface-hover);
}

.header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  width: 100%;
}

.header-label {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sort-indicator {
  color: var(--color-warning);
  font-size: 0.65rem;
  flex-shrink: 0;
  margin-left: auto;
  opacity: 0;
  min-width: 0.65rem;
}

.sort-indicator.sort-active {
  opacity: 1;
}

tbody td {
  padding: 0.75rem 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.03);
  color: var(--text-primary);
  font-size: 0.85rem;
  vertical-align: middle;
  position: relative;
}

tbody tr {
  transition: background-color 0.15s ease;
}

tbody tr:nth-child(even) {
  background-color: rgba(255, 255, 255, 0.015);
}

tbody tr:nth-child(odd) {
  background-color: transparent;
}

tbody tr:hover {
  background-color: var(--accent-soft) !important;
}

tbody tr.row-clickable {
  cursor: pointer;
}

/* Selectable mode - disable text selection so drag/shift-click select cleanly */
tbody.selectable {
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}

tbody.selectable td {
  cursor: pointer;
}

tbody td.cell-selected {
  background-color: rgba(115, 87, 210, 0.12) !important;
}

/* Reserve room on the right so the hover +/- buttons never cover content */
tbody td.has-cell-filters {
  padding-right: 2.75rem;
}

/* Leading caret (zoom-in) column */
th.caret-col,
td.caret-col {
  width: 2rem;
  padding: 0 0.25rem;
  text-align: center;
}

.caret-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  padding: 0;
  border: none;
  border-radius: var(--radius-xs);
  background: transparent;
  color: var(--text-faint);
  cursor: pointer;
  font-size: 0.75rem;
  transition: all var(--transition-fast) ease;
}

tbody tr:hover .caret-btn {
  color: var(--text-secondary);
}

.caret-btn:hover {
  background: var(--surface-hover);
  color: var(--text-primary);
}

/* Cell Content */
.cell-content {
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Table Footer */
.table-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  /* Reserve the selection-chip height so the footer doesn't grow/shift when a
     cell is selected (matches the 53px event-search footer). */
  min-height: 53px;
  padding: 0.5rem 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  flex-shrink: 0;
  background: var(--surface-card);
}

.footer-left,
.footer-right {
  flex: 1;
  min-width: 0;
}

.footer-left {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 0.5rem;
}

.footer-right {
  display: flex;
  justify-content: flex-end;
}

/* Search Container */
.search-container {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.25rem 0.5rem;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: var(--radius-sm);
  transition: var(--transition);
}

.search-container:focus-within {
  background: var(--surface-overlay-2);
  border-color: rgba(115, 87, 210, 0.4);
}

.search-container.has-value {
  background: var(--accent-soft-2);
  border-color: rgba(115, 87, 210, 0.3);
}

.search-icon {
  color: var(--text-faint);
  font-size: 0.75rem;
}

.search-input {
  background: transparent;
  border: none;
  outline: none;
  color: var(--text-primary);
  font-size: 0.8rem;
  width: 140px;
}

.search-input::placeholder {
  color: var(--text-faint);
}

.search-clear {
  background: none;
  border: none;
  color: var(--text-faint);
  padding: 0.15rem;
  cursor: pointer;
  border-radius: var(--radius-xs);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
}

.search-clear:hover {
  color: var(--text-primary);
  background: rgba(255, 255, 255, 0.1);
}

/* Result Info */
.result-info {
  color: var(--text-faint);
  font-size: 0.8rem;
  flex-shrink: 0;
}

/* Selection count chip (matches event search) */
.selection-count {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.25rem 0.5rem;
  background: var(--accent-soft-3);
  border: 1px solid rgba(115, 87, 210, 0.3);
  border-radius: var(--radius-sm);
  user-select: none;
}

.sel-count {
  background: var(--accent);
  color: white;
  padding: 0.1rem 0.4rem;
  border-radius: var(--radius-xs);
  font-size: 0.7rem;
  font-weight: 600;
  min-width: 1.25rem;
  text-align: center;
}

.sel-label {
  color: var(--text-primary);
  font-size: 0.75rem;
}

.sel-clear {
  background: none;
  border: none;
  color: var(--text-faint);
  padding: 0.15rem;
  cursor: pointer;
  border-radius: var(--radius-xs);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: var(--transition-fast);
  margin-left: 0.25rem;
}

.sel-clear:hover {
  background: rgba(239, 68, 68, 0.2);
  color: var(--color-error);
}

/* Applied selection filter tag (matches event-search active-filters) */
.active-filters {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.filter-tag {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.125rem 0.375rem;
  background: var(--accent-soft-3);
  border: 1px solid rgba(115, 87, 210, 0.3);
  border-radius: var(--radius-xs);
  font-size: 0.625rem;
}

.filter-field {
  color: var(--text-muted);
}

.filter-value {
  color: var(--text-primary);
}

.filter-remove {
  background: none;
  border: none;
  color: var(--text-faint);
  cursor: pointer;
  padding: 0.125rem;
  display: flex;
  align-items: center;
  font-size: 0.5rem;
  border-radius: 0.125rem;
  transition: var(--transition-fast);
}

.filter-remove:hover {
  color: var(--color-error);
  background: rgba(239, 68, 68, 0.2);
}
</style>
